import { useEffect, useState } from "react";
import {
    Box,
    Breadcrumbs,
    CircularProgress,
    Divider,
    FormControl,
    FormHelperText,
    FormLabel,
    Input,
    Link,
    Sheet,
    Typography,
} from "@mui/joy";
import { ChevronRight, Palette, Plus, Percent } from "lucide-react";
import { Link as RouterLink } from "react-router-dom";

import { Alert, Button } from "../../shared";
import { ColorForm } from "../Colors/components";
import {
    useAuth,
    useApiResponseParser,
    useAuthorization,
    useNotification,
    useAccentColors,
} from "../../hooks";
import { useColors, useMetadata } from "../../hooks/query";
import { ROLES } from "../../constants/permissions";
import {
    ORG_METADATA_SCOPE,
    ORG_SETTING_DEFAULTS,
    ORG_SETTING_KEYS,
    resolveOrgEstimatingDefaultsForEditor,
    type OrgEstimatingDefaults,
} from "../../constants/orgSettings";
import type { MetadataRow } from "../../hooks/query/useMetadata";
import type { ReactNode } from "react";
import {
    resolveAccentBackground,
    resolveAccentForeground,
} from "../../utils/accentColors";

function Breadcrumb() {
    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 0, ml: 1, mr: 2 }}
        >
            <Breadcrumbs separator={<ChevronRight size={18} />}>
                <Link
                    component={RouterLink}
                    to="/admin"
                    level="title-lg"
                    sx={{
                        fontWeight: 500,
                        textDecoration: "none",
                        color: "inherit",
                        "&:hover": { textDecoration: "underline" },
                    }}
                >
                    Admin
                </Link>
                <Typography level="body-md" sx={{ fontWeight: 500 }}>
                    General
                </Typography>
            </Breadcrumbs>
        </Box>
    );
}

function EstimatingDefaultsSection({
    organizationId,
    canEdit,
}: {
    organizationId: string;
    canEdit: boolean;
}) {
    const { showSuccess } = useNotification();
    const { parseErrorResponseData } = useApiResponseParser();
    const { useList, useCreate, useUpdate } = useMetadata();
    const listSvc = useList();
    const createSvc = useCreate();
    const updateSvc = useUpdate();

    const [values, setValues] = useState<OrgEstimatingDefaults>({
        ...ORG_SETTING_DEFAULTS,
    });
    const [savedValues, setSavedValues] = useState<OrgEstimatingDefaults>({
        ...ORG_SETTING_DEFAULTS,
    });
    const [rowByKey, setRowByKey] = useState<Partial<Record<string, MetadataRow>>>(
        {},
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState(false);

    const saving = createSvc.isPending || updateSvc.isPending;

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const rows = await listSvc.mutateAsync({
                OrganizationId: organizationId,
                Scope: ORG_METADATA_SCOPE,
                ScopeId: organizationId,
            });
            const map: Partial<Record<string, MetadataRow>> = {};
            for (const row of rows || []) {
                map[row.MetaKey] = row;
            }
            setRowByKey(map);
            const next = resolveOrgEstimatingDefaultsForEditor(rows);
            setValues(next);
            setSavedValues(next);
            setDirty(false);
        } catch (e) {
            setError(parseErrorResponseData(e) || "Failed to load settings");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (organizationId) void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    const setField = (key: keyof OrgEstimatingDefaults, raw: string) => {
        const n = Number(raw);
        setValues((prev) => {
            const next = {
                ...prev,
                [key]: Number.isFinite(n) ? n : prev[key],
            };
            setDirty(
                next.SalesTaxRate !== savedValues.SalesTaxRate ||
                    next.GeneralInsurance !== savedValues.GeneralInsurance ||
                    next.OverheadAmount !== savedValues.OverheadAmount,
            );
            return next;
        });
    };

    const save = async () => {
        setError(null);
        try {
            const entries: Array<[keyof OrgEstimatingDefaults, string, number]> = [
                ["SalesTaxRate", ORG_SETTING_KEYS.SalesTaxRate, values.SalesTaxRate],
                [
                    "GeneralInsurance",
                    ORG_SETTING_KEYS.GeneralInsurance,
                    values.GeneralInsurance,
                ],
                [
                    "OverheadAmount",
                    ORG_SETTING_KEYS.OverheadAmount,
                    values.OverheadAmount,
                ],
            ];

            // Always upsert all three keys so contract pricing can require them
            // without silent fallbacks.
            for (const [, metaKey, metaValue] of entries) {
                const existing = rowByKey[metaKey];
                if (existing?.Id) {
                    await updateSvc.mutateAsync({
                        Id: existing.Id,
                        MetaValue: metaValue,
                    });
                } else {
                    await createSvc.mutateAsync({
                        OrganizationId: organizationId,
                        Scope: ORG_METADATA_SCOPE,
                        ScopeId: organizationId,
                        MetaKey: metaKey,
                        MetaValue: metaValue,
                    });
                }
            }

            await load();
            showSuccess("Estimating defaults saved");
        } catch (e) {
            setError(parseErrorResponseData(e) || "Failed to save settings");
        }
    };

    const missingKeys = Object.values(ORG_SETTING_KEYS).some((key) => !rowByKey[key]);
    const canSave = canEdit && !loading && (dirty || missingKeys);

    return (
        <Sheet
            variant="outlined"
            sx={{
                borderRadius: "4px",
                p: { xs: 2, sm: 2.5 },
                bgcolor: "background.surface",
            }}
        >
            <Box mb={2}>
                <Typography level="title-md">Estimating defaults</Typography>
                <Typography
                    level="body-sm"
                    sx={{ color: "text.secondary", mt: 0.5 }}
                >
                    Organization-wide rates used for estimates and contracts.
                    These must be saved before contract pricing will work.
                </Typography>
            </Box>

            {error && (
                <Box mb={2}>
                    <Alert errorMessage={error} />
                </Box>
            )}

            {loading ? (
                <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size="sm" />
                </Box>
            ) : (
                <Box
                    display="grid"
                    gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr 1fr" }}
                    gap={2}
                >
                    <NumberSettingField
                        label="Sales tax rate"
                        helper="Applied to taxable line items"
                        value={values.SalesTaxRate}
                        disabled={!canEdit || saving}
                        onChange={(v) => setField("SalesTaxRate", v)}
                        endDecorator={<Percent size={14} />}
                    />
                    <NumberSettingField
                        label="General insurance"
                        helper="General insurance percent"
                        value={values.GeneralInsurance}
                        disabled={!canEdit || saving}
                        onChange={(v) => setField("GeneralInsurance", v)}
                        endDecorator={<Percent size={14} />}
                    />
                    <NumberSettingField
                        label="Overhead amount"
                        helper="Overhead percent for jobs"
                        value={values.OverheadAmount}
                        disabled={!canEdit || saving}
                        onChange={(v) => setField("OverheadAmount", v)}
                        endDecorator={<Percent size={14} />}
                    />
                </Box>
            )}

            {canEdit && (
                <Box display="flex" justifyContent="flex-end" mt={2.5}>
                    <Button
                        variant="solid"
                        color="primary"
                        loading={saving}
                        disabled={loading || !canSave}
                        onClick={save}
                    >
                        Save defaults
                    </Button>
                </Box>
            )}

            {!canEdit && (
                <Typography
                    level="body-xs"
                    sx={{ color: "text.tertiary", mt: 2 }}
                >
                    Only Admin can change these values.
                </Typography>
            )}
        </Sheet>
    );
}

function NumberSettingField({
    label,
    helper,
    value,
    disabled,
    onChange,
    endDecorator,
}: {
    label: string;
    helper: string;
    value: number;
    disabled?: boolean;
    onChange: (raw: string) => void;
    endDecorator?: ReactNode;
}) {
    return (
        <FormControl size="md">
            <FormLabel
                sx={{
                    textTransform: "uppercase",
                    fontSize: "12px",
                    fontWeight: "xl",
                    letterSpacing: "0.5px",
                }}
            >
                {label}
            </FormLabel>
            <Typography
                level="body-xs"
                color="neutral"
                sx={{ mt: -0.5, mb: 0.5, fontSize: "12px" }}
            >
                {helper}
            </Typography>
            <Input
                type="number"
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                endDecorator={endDecorator}
                slotProps={{ input: { step: "0.01", min: "0" } }}
                sx={{
                    bgcolor: "background.surface",
                    borderRadius: "4px",
                    boxShadow: "none",
                }}
            />
            <FormHelperText sx={{ display: "none" }} />
        </FormControl>
    );
}

function AccentColorsSection({ canEdit }: { canEdit: boolean }) {
    const { useCreate, useUpdate, useDelete } = useColors();
    const createColorSvc = useCreate();
    const updateColorSvc = useUpdate();
    const deleteColorSvc = useDelete();
    const { colors, mode, isLoading, refetch } = useAccentColors();
    const { parseErrorResponseData } = useApiResponseParser();
    const { showSuccess } = useNotification();

    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [editingColor, setEditingColor] = useState<any | null>(null);

    const handleCreate = async (colorData: any) => {
        try {
            await createColorSvc.mutateAsync(colorData);
            await refetch();
            setShowForm(false);
            showSuccess("Color created");
        } catch (e) {
            setError(parseErrorResponseData(e) || "Failed to create color");
        }
    };

    const handleUpdate = async (colorData: any) => {
        try {
            await updateColorSvc.mutateAsync(colorData);
            await refetch();
            setShowForm(false);
            setEditingColor(null);
            showSuccess("Color updated");
        } catch (e) {
            setError(parseErrorResponseData(e) || "Failed to update color");
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await deleteColorSvc.mutateAsync({ id });
            await refetch();
            showSuccess("Color deleted");
        } catch (e) {
            setError(parseErrorResponseData(e) || "Failed to delete color");
        }
    };

    return (
        <Sheet
            variant="outlined"
            sx={{
                borderRadius: "4px",
                p: { xs: 2, sm: 2.5 },
                bgcolor: "background.surface",
            }}
        >
            <Box
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
                gap={2}
                mb={2}
            >
                <Box>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Palette size={18} />
                        <Typography level="title-md">
                            Calendar accent colors
                        </Typography>
                    </Box>
                    <Typography
                        level="body-sm"
                        sx={{ color: "text.secondary", mt: 0.5 }}
                    >
                        Each color has a light and dark hex. Calendar events use
                        the shade for the active theme
                        {mode ? ` (currently ${mode})` : ""}.
                    </Typography>
                </Box>
                {canEdit && (
                    <Button
                        size="sm"
                        variant="outlined"
                        startDecorator={<Plus size={14} />}
                        onClick={() => {
                            setEditingColor(null);
                            setShowForm(true);
                        }}
                    >
                        Add color
                    </Button>
                )}
            </Box>

            {error && (
                <Box mb={2}>
                    <Alert errorMessage={error} />
                </Box>
            )}

            {isLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size="sm" />
                </Box>
            ) : (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns:
                            "repeat(auto-fill, minmax(180px, 1fr))",
                        gap: 1.5,
                    }}
                >
                    {colors.map((color) => {
                        const activeBg = resolveAccentBackground(color, mode);
                        const activeFg = resolveAccentForeground(color, mode);
                        return (
                            <Box
                                key={color.Id}
                                sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: "4px",
                                    overflow: "hidden",
                                    bgcolor: "background.level1",
                                }}
                            >
                                <Box
                                    sx={{
                                        height: 40,
                                        bgcolor: activeBg,
                                        color: activeFg,
                                        display: "flex",
                                        alignItems: "center",
                                        px: 1.25,
                                        fontSize: "12px",
                                        fontWeight: 600,
                                    }}
                                >
                                    Active theme
                                </Box>
                                <Box display="flex" height={22}>
                                    <Box
                                        flex={1}
                                        sx={{
                                            bgcolor: color.LightHex || "#e5e7eb",
                                        }}
                                        title={`Light: ${color.LightHex}`}
                                    />
                                    <Box
                                        flex={1}
                                        sx={{
                                            bgcolor: color.DarkHex || "#374151",
                                        }}
                                        title={`Dark: ${color.DarkHex}`}
                                    />
                                </Box>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    gap={1}
                                    sx={{ px: 1.25, py: 1 }}
                                >
                                    <Typography
                                        level="body-sm"
                                        sx={{ fontWeight: 500, minWidth: 0 }}
                                        noWrap
                                    >
                                        {color.Name}
                                    </Typography>
                                    {canEdit && (
                                        <Box
                                            display="flex"
                                            gap={0.5}
                                            flexShrink={0}
                                        >
                                            <Button
                                                size="sm"
                                                variant="plain"
                                                onClick={() => {
                                                    setEditingColor(color);
                                                    setShowForm(true);
                                                }}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="plain"
                                                color="danger"
                                                onClick={() =>
                                                    handleDelete(
                                                        Number(color.Id),
                                                    )
                                                }
                                            >
                                                Delete
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                    {!colors.length && (
                        <Typography
                            level="body-sm"
                            sx={{ color: "text.secondary" }}
                        >
                            No accent colors yet. Add pairs for calendar events.
                        </Typography>
                    )}
                </Box>
            )}

            {showForm && (
                <ColorForm
                    color={editingColor}
                    onSubmit={editingColor ? handleUpdate : handleCreate}
                    onClose={() => {
                        setShowForm(false);
                        setEditingColor(null);
                    }}
                />
            )}
        </Sheet>
    );
}

export default function GeneralSettings() {
    const { selectedOrganization } = useAuth();
    const { hasRole } = useAuthorization();
    const canEdit =
        hasRole(ROLES.ADMIN_OWNER) || hasRole(ROLES.SUPER_ADMIN);
    const organizationId = selectedOrganization?.Id;

    return (
        <Box
            flex={1}
            display="flex"
            flexDirection="column"
            maxHeight="100%"
            sx={{ overflow: "hidden", minWidth: 0, minHeight: 0 }}
        >
            <Breadcrumb />
            <Divider sx={{ mt: 0, ml: 0 }} />

            <Box flex={1} sx={{ minWidth: 0, overflow: "auto" }}>
                <Box
                    sx={{
                        px: { xs: 2, sm: 3 },
                        pt: 2.5,
                        pb: 1,
                        maxWidth: 920,
                    }}
                >
                    <Typography level="title-lg">General settings</Typography>
                    <Typography
                        level="body-sm"
                        sx={{ color: "text.secondary", mt: 0.5 }}
                    >
                        Organization defaults for estimating and accent colors
                        {selectedOrganization?.Name
                            ? ` · ${selectedOrganization.Name}`
                            : ""}
                        .
                    </Typography>
                </Box>

                <Box
                    sx={{
                        px: { xs: 2, sm: 3 },
                        pb: 4,
                        maxWidth: 920,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2.5,
                    }}
                >
                    {!organizationId ? (
                        <Alert errorMessage="Select an organization to manage general settings." />
                    ) : (
                        <EstimatingDefaultsSection
                            organizationId={organizationId}
                            canEdit={canEdit}
                        />
                    )}

                    <AccentColorsSection canEdit={canEdit} />
                </Box>
            </Box>
        </Box>
    );
}
